import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import {
  Pin,
  Pencil,
  Accessibility,
  Share2,
  Ticket,
  CalendarCheck,
  DoorOpen,
  Lock,
  Sunrise,
  Sun,
  Moon,
} from 'lucide-react';
import { VendorCardProps, ExpandedVendor, SelectionState } from '../../types/types';
import type { LucideIcon } from 'lucide-react';
import { logger } from '../../lib/logger';
import { buildCustomUTMUrl } from '../../lib/utils';
import '../../styles/variables.css';
import { AuthContext } from '../../contexts/AuthContext';
import { auth } from '../../lib/firebase';
import { useGuestLists } from '../../hooks/useGuestLists';
import { format } from 'date-fns';
import panelBackgroundImage from '../../assets/panel-background.png';
const UNVERIFIED_PIN_LIMIT = 2; // max pinned events for unverified email accounts
interface IconData {
  key: string;
  icon: LucideIcon;
  title: string;
}

const getCardVisualState = (
  vendor: ExpandedVendor,
  selection?: SelectionState,
  isSelected?: boolean
) => {
  // If new selection state is provided, use it
  if (selection) {
    const isThisCardSelected = selection.currentDateDisplayId === vendor.displayID;
    const isVendorSelected = selection.vendorId === vendor.id;
    const isOtherInstance = isVendorSelected && !isThisCardSelected;

    return {
      isSelected: isThisCardSelected,
      isRelated: isOtherInstance,
      isDimmed: selection.vendorId !== null && !isVendorSelected,
    };
  }

  // Fallback to simple isSelected prop
  return {
    isSelected: isSelected || false,
    isRelated: false,
    isDimmed: false,
  };
};

const VendorCard: React.FC<VendorCardProps> = ({
  vendor,
  isSelected,
  onClick,
  selection,
  isPopup: _isPopup = false,
  onSignUpModalRequest: _onSignUpModalRequest,
  onShareEvent,
  onPinSuccess,
  showToast,
  activeListItems,
  onPinEvent,
  onUnpinEvent,
  onEditEvent,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [linkHandledByTouch, setLinkHandledByTouch] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
  const [hasMoved, setHasMoved] = useState(false);

  // Pin functionality state
  const [isPinning, setIsPinning] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [pinnedItemId, setPinnedItemId] = useState<number | null>(null);
  const verifyToastActiveRef = useRef(false);
  const authContext = useContext(AuthContext);
  // Fall back to firebase auth directly so pin works in map popups (separate React roots)
  const currentUser = authContext?.currentUser ?? auth.currentUser;
  const guestLists = useGuestLists();

  const cardVisualState = useMemo(
    () => getCardVisualState(vendor, selection, isSelected),
    [vendor.displayID, selection?.vendorId, selection?.currentDateDisplayId, isSelected]
  );

  const { isSelected: cardSelected, isRelated, isDimmed } = cardVisualState;

  // Sync pin state from activeListItems prop (handles initial load / navigation between cards)
  useEffect(() => {
    const eventDate = format(vendor.displayDate, 'yyyy-MM-dd');

    if (currentUser) {
      if (activeListItems) {
        const existing = activeListItems.find(
          item => item.vendor_id === vendor.id && item.event_date === eventDate
        );
        setIsPinned(!!existing);
        setPinnedItemId(existing?.id ?? null);
      }
    } else {
      setIsPinned(guestLists.isEventPinned(vendor.id, eventDate));
    }
  }, [vendor.id, vendor.displayDate, currentUser, activeListItems]); // guestLists intentionally omitted — stable ref

  // Handle pin button click
  const handlePinClick = async (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isPinning) return; // prevent concurrent pin/unpin operations

    const eventDate = format(vendor.displayDate, 'yyyy-MM-dd');

    // === UNPIN ===
    if (isPinned) {
      if (currentUser && onUnpinEvent) {
        // Use locally-stored pinnedItemId (doesn't depend on prop being fresh)
        const itemId =
          pinnedItemId ??
          activeListItems?.find(i => i.vendor_id === vendor.id && i.event_date === eventDate)?.id;

        if (!itemId) {
          showToast?.('could not find saved event — try refreshing', 'error', 3000);
          return;
        }

        setIsPinning(true);
        const ok = await onUnpinEvent(itemId);
        setIsPinning(false);

        if (ok) {
          setIsPinned(false);
          setPinnedItemId(null);
          showToast?.('removed from your list', 'info', 2000);
        } else {
          showToast?.('could not remove — try again', 'error', 3000);
        }
      }
      return;
    }

    // === VERIFICATION GATE (unverified email users only) ===
    // Gate is on number of currently pinned events, not click count
    if (
      currentUser &&
      !currentUser.emailVerified &&
      currentUser.providerData[0]?.providerId === 'password'
    ) {
      const currentPinCount = activeListItems?.length ?? 0;
      if (currentPinCount >= UNVERIFIED_PIN_LIMIT) {
        if (!verifyToastActiveRef.current) {
          verifyToastActiveRef.current = true;
          showToast?.(
            'verify your email to keep saving — open the console to resend the link',
            'warning',
            5000
          );
          setTimeout(() => {
            verifyToastActiveRef.current = false;
          }, 5000);
        }
        return;
      }
    }

    // === PIN ===
    setIsPinning(true);

    try {
      if (currentUser && onPinEvent) {
        const newItemId = await onPinEvent(vendor.id, eventDate, vendor.name, vendor.category);
        if (newItemId !== null) {
          setIsPinned(true);
          setPinnedItemId(newItemId);
          showToast?.('saved to your list', 'success', 2000);
          onPinSuccess?.();
        } else {
          showToast?.('could not save — try again', 'error', 3000);
        }
      } else if (!currentUser) {
        // === GUEST: use localStorage ===
        const listTitle = `${vendor.name} - ${format(vendor.displayDate, 'MMM d')}`;
        const guestList = guestLists.createList({
          title: listTitle,
          description: `Saved from ${format(vendor.displayDate, 'EEEE, MMMM d, yyyy')}`,
        });
        guestLists.addItemToList(guestList.id, {
          vendor_id: vendor.id,
          event_date: eventDate,
          comment: '',
        });
        setIsPinned(true);
        onPinSuccess?.();
      }
    } catch (error) {
      logger.error('Error pinning event:', error);
      showToast?.('could not save — try again', 'error', 3000);
    } finally {
      setIsPinning(false);
    }
  };

  useEffect(() => {
    if (isRelated) {
      logger.log('Card showing as related:', {
        vendorName: vendor.name,
        vendorId: vendor.id,
        displayId: vendor.displayID,
        selectionVendorId: selection?.vendorId,
        selectionCurrentDisplayId: selection?.currentDateDisplayId,
        selectionAllDisplayIds: selection?.allDisplayIds,
      });
    }
  }, [
    isRelated,
    vendor.name,
    vendor.id,
    vendor.displayID,
    selection?.vendorId,
    selection?.currentDateDisplayId,
  ]);

  // Auto-expand when selected
  useEffect(() => {
    setIsExpanded(cardSelected);
  }, [cardSelected]);

  const getAudienceIconData = (audience: string[] = []): IconData[] => {
    const iconData: IconData[] = [];

    // Accessibility icon if sensory or physical accessibility present
    if (audience.some(a => ['sensory', 'physical accessibility'].includes(a))) {
      iconData.push({
        key: 'accessibility',
        icon: Accessibility,
        title: 'Accessible',
      });
    }

    return iconData;
  };

  // Icon carries the status color; the label stays --color-content-muted — the accent
  // hues here (ember, clay) only clear WCAG's 3:1 non-text/icon minimum on light surfaces,
  // not the 4.5:1 body-text minimum, so they're icon-only (see docs/design-systems.md §4).
  const getRequirementConfig = () => {
    const reqs = vendor.metadata.requirements || [];
    const utmUrl = buildCustomUTMUrl(vendor.metadata.media_link);
    if (reqs.includes('Paid')) {
      return { icon: Ticket, label: 'tickets', color: 'var(--color-yellow)', utmUrl };
    }
    if (reqs.some(r => ['Register', 'registration required'].includes(r))) {
      return { icon: CalendarCheck, label: 'rsvp', color: 'var(--color-clay)', utmUrl };
    }
    return { icon: DoorOpen, label: 'drop in', color: 'var(--color-primary)', utmUrl };
  };

  // Small tinted icon next to the time, not a background wash — same reasoning as above.
  // Evening/night uses forest-deep rather than a blue/indigo hue, since none exists in the palette.
  const getTimeOfDayIndicator = (): { icon: LucideIcon; color: string } | null => {
    const timeStr = vendor.localized_schedule?.start_time || vendor.metadata?.start_time;
    if (!timeStr) return null;
    const hours = timeStr.includes(':')
      ? Number(timeStr.split(':')[0])
      : Number(timeStr.slice(0, 2));
    if (hours >= 5 && hours < 12) return { icon: Sunrise, color: 'var(--color-yellow)' }; // morning
    if (hours >= 12 && hours < 17) return { icon: Sun, color: 'var(--color-clay)' }; // afternoon
    return { icon: Moon, color: 'var(--color-forest-deep)' }; // evening / night
  };

  const formatTimeDisplay = () => {
    const fmt = (t: { time: string; period: string }) => `${t.time}${t.period.toLowerCase()}`;
    const start = formatTime(vendor.localized_schedule?.start_time || vendor.metadata?.start_time);
    const end = formatTime(vendor.localized_schedule?.end_time || vendor.metadata?.end_time);
    return `${fmt(start)} — ${fmt(end)}`;
  };

  const getAgeRestrictionText = (audience: string[] = []) => {
    if (audience.includes('21+')) return '21+';
    if (audience.includes('18+')) return '18+';
    return null;
  };

  const formatTime = (timeString: string | undefined): { time: string; period: string } => {
    if (!timeString) {
      return { time: 'TBD', period: '' };
    }

    const [hours, minutes] = timeString.includes(':')
      ? timeString.split(':').map(Number)
      : [Number(timeString.slice(0, 2)), Number(timeString.slice(2))];

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes === 0 ? ':00' : `:${minutes.toString().padStart(2, '0')}`;

    return {
      time: `${displayHours}${displayMinutes}`,
      period,
    };
  };

  return (
    <div className="relative my-0.5">
      {/* Square Card Design */}
      <div
        data-vendor-card="true"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        className={`
          w-full
          p-0 rounded-xl cursor-pointer transition-all duration-500 ease-out shadow-token-lg
          ${isExpanded ? 'rounded-b-none' : 'rounded-b-xl border-b-2 aspect-square'}
          ${cardSelected ? 'bg-highlight/20' : ''}
          ${isRelated ? 'bg-highlight/10 ring-1 ring-highlight/60' : ''}
          ${isDimmed ? 'contrast-125 brightness-50' : ''}
          ${!cardSelected && !isRelated && !isDimmed ? 'bg-surface hover:bg-surface-subtle' : ''}
          flex flex-col overflow-hidden
        `}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          onClick();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        onTouchStart={e => {
          e.stopPropagation();
          const touch = e.touches[0];
          setTouchStartPos({ x: touch.clientX, y: touch.clientY });
          setHasMoved(false);
        }}
        onTouchMove={e => {
          if (!touchStartPos) return;
          const touch = e.touches[0];
          const deltaX = Math.abs(touch.clientX - touchStartPos.x);
          const deltaY = Math.abs(touch.clientY - touchStartPos.y);
          if (deltaX > 10 || deltaY > 10) setHasMoved(true);
        }}
        onTouchEnd={e => {
          e.stopPropagation();
          e.preventDefault();
          if (!hasMoved) onClick(); // Only trigger if no movement
          setTouchStartPos(null);
        }}
        style={{
          width: 'var(--card-width)',
          maxWidth: 'var(--card-width)',
          height: isExpanded ? '80px' : 'var(--card-width)',
          touchAction: 'manipulation', // Prevents double-tap zoom and iOS gestures
          WebkitTouchCallout: 'none', // Prevents iOS callout menu
          WebkitUserSelect: 'none', // Prevents text selection
          userSelect: 'none',
          WebkitTapHighlightColor: 'rgba(245, 158, 11, 0.1)', // Subtle highlight on tap
        }}
      >
        {/* Brand Logo as Background with Overlay */}
        <div className="relative w-full flex-grow flex flex-col">
          {/* Logo Background */}
          <div
            role="img"
            aria-label={
              vendor.metadata.event_image?.[0] ? `Event image for ${vendor.name}` : vendor.name
            }
            className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-surface z-0"
            style={{
              backgroundImage:
                vendor.metadata.event_image && vendor.metadata.event_image.length > 0
                  ? `url(${vendor.metadata.event_image[0].url})`
                  : vendor.metadata.brand_logo && vendor.metadata.brand_logo.length > 0
                    ? `url(${vendor.metadata.brand_logo[0].url})`
                    : `url(${panelBackgroundImage})`,
              ...(vendor.metadata.event_image?.[0]?.url?.startsWith('data:')
                ? { imageRendering: 'pixelated' }
                : {}),
            }}
          >
            {/* Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          </div>

          {/* Icon area - top right of card - horizontal layout */}
          <div className="absolute top-1 right-1 flex flex-col gap-1 items-end">
            <div className="flex flex-row gap-1">
              {/* Private event badge — only visible to the owner */}
              {vendor.visibility === 'private' && vendor.is_own_event && (
                <div
                  aria-label="private event"
                  className="flex items-center justify-center"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  <Lock size={10} color="rgba(255,255,255,0.8)" />
                </div>
              )}
              {/* Repeat icon */}
              {vendor.schedule?.repeat && vendor.schedule.repeat === 'weekly' && (
                <div
                  aria-label="repeats weekly"
                  className="text-white text-xs flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--color-forest)',
                    borderColor: 'var(--color-surface)',
                    borderWidth: '1px',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  w
                </div>
              )}
              {vendor.schedule?.repeat && vendor.schedule.repeat === 'monthly' && (
                <div
                  aria-label="repeats monthly"
                  className="text-white text-xs flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--color-forest)',
                    borderColor: 'var(--color-surface)',
                    borderWidth: '1px',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  m
                </div>
              )}
            </div>

            {/* Age restriction badge - only shown when expanded */}
            {isExpanded && getAgeRestrictionText(vendor.metadata.audience) && (
              <div
                aria-label={`Age restriction: ${getAgeRestrictionText(vendor.metadata.audience)}`}
                className="text-white flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--color-forest)',
                  borderColor: 'var(--color-surface)',
                  borderWidth: '1px',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '8px',
                }}
              >
                {getAgeRestrictionText(vendor.metadata.audience)}
              </div>
            )}
          </div>

          {/* Content with improved vertical spacing */}
          <div className="relative z-10 h-full flex flex-col p-2.5">
            {/* Title area with more space */}
            <div className="w-full h-full flex flex-col justify-end">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-sm max-w-[120px] text-white drop-shadow-lg [text-shadow:_1px_1px_3px_rgb(0_0_0_/_90%)]">
                  {vendor.name}
                </h3>
                {vendor.uses_gps && (
                  <span aria-hidden="true" className="relative inline-flex h-1.5 w-1.5">
                    <span
                      className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                      style={{ backgroundColor: 'var(--color-location-ghost)' }}
                    ></span>
                    <span
                      className="relative inline-flex rounded-full h-1.5 w-1.5"
                      style={{ backgroundColor: 'var(--color-location)' }}
                    ></span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded content - appears below the card when selected */}
      <div
        className={`
          bg-surface overflow-hidden transition-all duration-500 ease-out shadow-token-md
          rounded-b-xl flex flex-col
          ${isExpanded ? 'pt-2 pb-0 opacity-100' : 'opacity-0 py-0'}
          ${cardSelected ? 'bg-highlight/10' : ''}
        `}
        style={{
          width: 'var(--card-width)',
          maxWidth: 'var(--card-width)',
          height: isExpanded ? 'calc(var(--card-width) + 28px)' : '0',
          pointerEvents: isExpanded ? 'auto' : 'none',
        }}
        onClick={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
      >
        {/* Description with Fixed Height and Conditional Marquee */}
        {vendor.metadata.description && (
          <div
            key={isExpanded ? 'expanded' : 'collapsed'}
            className="description-container mb-2 px-3"
            ref={el => {
              if (el) {
                const textEl = el.querySelector('.description-text') as HTMLElement;
                if (textEl) {
                  const containerHeight = el.clientHeight;
                  const textHeight = textEl.scrollHeight;
                  const hasOverflow = textHeight > containerHeight;

                  if (hasOverflow) {
                    el.classList.add('has-overflow');

                    // Calculate scroll distance (negative to scroll up)
                    // Leave some padding at the bottom so last line is visible
                    const scrollDistance = -(textHeight - containerHeight + 4);
                    textEl.style.setProperty('--scroll-distance', `${scrollDistance}px`);

                    // Calculate duration: 2.5s pause + scroll time + 2.5s pause
                    // Speed: 30 pixels per second for comfortable reading
                    const scrollTime = Math.abs(scrollDistance) / 30;
                    const totalDuration = 2.5 + scrollTime + 2.5;
                    textEl.style.setProperty('--marquee-duration', `${totalDuration}s`);
                  } else {
                    el.classList.remove('has-overflow');
                  }
                }
              }
            }}
          >
            <div className="description-text text-xs text-content leading-4 animate-marquee-vertical">
              {vendor.metadata.description}
            </div>
          </div>
        )}

        {/* Time display + audience icons */}
        {(() => {
          const audienceIcons = getAudienceIconData(vendor.metadata.audience);
          const timeOfDay = getTimeOfDayIndicator();
          const TimeOfDayIcon = timeOfDay?.icon;
          return (
            <div className="flex items-center justify-between px-3 py-2">
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                {TimeOfDayIcon && <TimeOfDayIcon size={12} color={timeOfDay!.color} />}
                <div
                  style={{
                    fontSize: '0.6rem',
                    letterSpacing: '0.5px',
                    textTransform: 'lowercase',
                    color: 'var(--color-content-muted)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatTimeDisplay()}
                </div>
              </div>
              {audienceIcons.length > 0 && (
                <div className="flex gap-1">
                  {audienceIcons.map(({ key, icon: IconComponent, title }) => (
                    <div key={key} title={title} className="bg-primary/70 rounded-full p-1">
                      <IconComponent size={10} className="text-white" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Action bar: Save | Requirement | Share */}
        {(() => {
          const req = getRequirementConfig();
          const ReqIcon = req.icon;
          const actionLabelStyle: React.CSSProperties = {
            fontSize: '10px',
            letterSpacing: '0.5px',
            textTransform: 'lowercase',
            lineHeight: 1,
          };
          const reqContent = (
            <>
              <ReqIcon size={13} color={req.utmUrl ? req.color : 'var(--color-content-muted)'} />
              <span style={{ ...actionLabelStyle, color: 'var(--color-content-muted)' }}>
                {req.label}
              </span>
            </>
          );
          return (
            <div
              className="border-t border-border flex items-stretch mt-auto"
              style={{ minHeight: '52px' }}
            >
              {/* Save / Edit */}
              {vendor.is_own_event ? (
                <button
                  aria-label="edit event"
                  onTouchEnd={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onEditEvent) onEditEvent(vendor.id);
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    if (onEditEvent) onEditEvent(vendor.id);
                  }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 transition-colors"
                  style={{
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <Pencil size={13} color="var(--color-primary)" />
                  <span style={{ ...actionLabelStyle, color: 'var(--color-primary)' }}>edit</span>
                </button>
              ) : (
                <button
                  aria-label={isPinned ? 'remove from saved' : 'save to list'}
                  aria-busy={isPinning}
                  disabled={isPinning}
                  onTouchEnd={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (currentUser) {
                      handlePinClick(e);
                    } else {
                      showToast?.('sign in to save places', 'info');
                    }
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    if (currentUser) {
                      handlePinClick(e);
                    } else {
                      showToast?.('sign in to save places', 'info');
                    }
                  }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 transition-colors"
                  style={{
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    opacity: isPinning ? 0.5 : 1,
                    cursor: isPinning ? 'wait' : 'pointer',
                  }}
                >
                  <Pin
                    size={13}
                    color={isPinned ? 'var(--color-success)' : 'var(--color-content-muted)'}
                    fill={isPinned ? 'var(--color-success)' : 'none'}
                  />
                  <span
                    style={{
                      ...actionLabelStyle,
                      color: isPinned ? 'var(--color-success)' : 'var(--color-content-muted)',
                    }}
                  >
                    {isPinned ? 'saved' : 'save'}
                  </span>
                </button>
              )}

              <div className="w-px bg-border self-stretch" />

              {/* Requirement */}
              {req.utmUrl ? (
                <a
                  href={req.utmUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onTouchEnd={(e: React.TouchEvent<HTMLAnchorElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setLinkHandledByTouch(true);
                    window.open(req.utmUrl!, '_blank', 'noopener,noreferrer');
                    setTimeout(() => setLinkHandledByTouch(false), 500);
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    if (linkHandledByTouch) {
                      e.preventDefault();
                      return;
                    }
                  }}
                  className="flex-1 flex flex-col items-center justify-center gap-1"
                  style={{
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {reqContent}
                </a>
              ) : (
                <div
                  className="flex-1 flex flex-col items-center justify-center gap-1"
                  style={{ opacity: 0.45 }}
                >
                  {reqContent}
                </div>
              )}

              <div className="w-px bg-border self-stretch" />

              {/* Share */}
              <button
                aria-label="share event"
                onTouchEnd={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  onShareEvent?.(vendor.id, vendor.displayDate, vendor.coordinates);
                }}
                onClick={e => {
                  e.stopPropagation();
                  onShareEvent?.(vendor.id, vendor.displayDate, vendor.coordinates);
                }}
                className="flex-1 flex flex-col items-center justify-center gap-1 transition-colors"
                style={{
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Share2 size={13} color="var(--color-content-muted)" />
                <span style={{ ...actionLabelStyle, color: 'var(--color-content-muted)' }}>
                  share
                </span>
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default VendorCard;
